import { useEffect, useRef, useState } from 'react';
import { reduirePhoto, type PhotoReduite } from '../../terrain/photos';
import { requete } from '../../api/client';
import { ErreurApi, messageErreur } from '../../api/erreurs';

/** Même stockage privé, contrôles d'accès et empreinte que les pièces jointes existantes. */
export async function joindrePhoto(
  type: 'incident' | 'intervention',
  id: string,
  photo: PhotoReduite,
) {
  const chemin = `/pieces-jointes/objets/${type}/${id}`;
  const retrouver = async () => {
    const liste = await requete<{ elements: { id: string; empreinte: string }[] }>(chemin);
    return liste.elements.find((p) => p.empreinte === photo.empreinte)?.id;
  };
  const existante = await retrouver();
  if (existante) return existante;
  const data = new FormData();
  data.append('fichier', photo.fichier, `photo-${type}-${photo.empreinte.slice(0, 12)}.jpg`);
  try {
    return (await requete<{ id: string }>(chemin, { methode: 'POST', corps: data })).id;
  } catch (e) {
    if (e instanceof ErreurApi && e.statut === 409) {
      const idExistant = await retrouver();
      if (idExistant) return idExistant;
    }
    throw e;
  }
}

export function PrisePhoto({
  titre,
  photo,
  onChange,
  onBusy,
  disabled = false,
}: {
  titre: string;
  photo: PhotoReduite | null;
  onChange: (photo: PhotoReduite | null) => void;
  onBusy: (busy: boolean) => void;
  disabled?: boolean;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const galerie = useRef<HTMLInputElement>(null);
  const [apercu, setApercu] = useState('');
  const [erreur, setErreur] = useState('');
  const [preparation, setPreparation] = useState(false);
  useEffect(() => {
    if (!photo) {
      setApercu('');
      return;
    }
    const url = URL.createObjectURL(photo.fichier);
    setApercu(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);
  async function choisir(source?: File) {
    if (!source) return; // Annulation de l'appareil photo : conserver la sélection précédente.
    setErreur('');
    setPreparation(true);
    onBusy(true);
    try {
      const resultat = await reduirePhoto(source);
      if (resultat.taille > 5 * 1024 * 1024)
        throw new Error('La photo réduite dépasse 5 Mio. Choisissez une image plus petite.');
      onChange(resultat);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setPreparation(false);
      onBusy(false);
    }
  }
  return (
    <fieldset disabled={disabled || preparation}>
      <legend>{titre}</legend>
      <p>
        Sur téléphone, ouvrez l’appareil photo puis confirmez la prise. Sur ordinateur, choisissez
        une image. Photo facultative, réduite avant envoi.
      </p>
      <input
        ref={camera}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        hidden
        aria-label={`Appareil photo - ${titre}`}
        onChange={(e) => {
          void choisir(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <input
        ref={galerie}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        aria-label={`Galerie - ${titre}`}
        onChange={(e) => {
          void choisir(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <button type="button" onClick={() => camera.current?.click()}>
        Prendre une photo
      </button>{' '}
      <button type="button" onClick={() => galerie.current?.click()}>
        Choisir dans la galerie
      </button>
      {preparation && <p role="status">Préparation de la photo…</p>}
      {erreur && <p role="alert">{erreur}</p>}
      {apercu && (
        <div>
          <img
            src={apercu}
            alt={`Aperçu - ${titre}`}
            style={{
              display: 'block',
              maxWidth: '100%',
              width: 260,
              maxHeight: 220,
              objectFit: 'contain',
              marginBlock: 12,
            }}
          />
          <p>Photo prête à joindre avec l’enregistrement.</p>
          <button type="button" onClick={() => onChange(null)}>
            Retirer cette sélection
          </button>
        </div>
      )}
    </fieldset>
  );
}
